/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import axios from 'axios';

import { Controller } from '@context';
import { CustomerState } from '@state';
import { fetchTax } from './fetch-tax';
import { ItemizedLineItem, PromotionalItemData } from '@rta/rta-blueprint-library';
import { VerifiedQuoteItem } from './verify-quote-items';

export type PriceOverride = {
	sku: string;
	type: string;
	selectedPrice: 'hubspot' | 'tool';
	hubspotPrice: number | null;
	toolPrice: number;
};

export type DealAddress = {
	address?: string;
	city?: string;
	state?: string;
	zip?: string;
};

export type SaveQuoteParams = {
	controller: Controller,
	customer: CustomerState,
	dealAddress?: DealAddress,
	dealId: string,
	link: string,
	getToken: () => Promise<string>,
	setQuoteId: (quoteId: string) => void,
	setError?: (error: string) => void,
	priceOverrides?: PriceOverride[],
	quoteName?: string,
	publish?: boolean
};

export const saveQuote = async (params: SaveQuoteParams): Promise<[string | null, string | null]> => {
	// eslint-disable-next-line no-empty-function, @typescript-eslint/no-empty-function
	const { controller, customer, dealAddress, dealId, link, getToken, setQuoteId, setError = () => {}, priceOverrides = [], quoteName, publish = false } = params;

	const kitchenData = controller?.kitchen ?? null;
	const itemData = kitchenData?.itemizedMsrp ?? null;
	const islandData = itemData?.islands ?? null;
	const promotionalData = kitchenData?.promotionalSkus?.children ?? null;

	const getPrice = (sku: string, type: string, defaultPrice: number): number => {
		const override = priceOverrides.find((o) => o.sku === sku && o.type === type);
		if (override) {
			if (override.selectedPrice === 'hubspot' && override.hubspotPrice !== null) {
				return override.hubspotPrice;
			}
			if (override.selectedPrice === 'tool') {
				return override.toolPrice;
			}
		}
		return defaultPrice;
	};

	const items: Array<{ type: string, quantity: number; amount: number; sku: string; hsProductId: string | null; discount: number, order: number, isFuelVariant?: boolean, name?: string, customSku?: string }> = [];
	const discounts: Array<{ sku: string; duration: string; type: string; amount: string; order: string; }> = [];
	const fees: Array<{ label: string; type: string; value: string; }> = [];
	let order = 0;
	islandData?.forEach((item) => {
		items.push({
			type: 'island',
			hsProductId: item.hsProductId ?? null,
			quantity: item.qty,
			amount: getPrice(item.sku, 'island', item.each),
			sku: item.sku,
			discount: 0,
			order,
			isFuelVariant: false
		});
		order++;
	});

	itemData.countertops.filter((countertop: ItemizedLineItem) => !countertop.sku.startsWith('RTA-Chiseled')).forEach((item) => {
		items.push({
			type: 'countertop',
			hsProductId: item.hsProductId ?? null,
			quantity: item.qty,
			amount: getPrice(item.sku, 'countertop', item.each),
			sku: item.sku,
			discount: 0,
			order,
			isFuelVariant: false
		});
		order++;
	});

	itemData?.backsplashes?.forEach((item) => {
		items.push({
			type: 'backsplash',
			hsProductId: item.hsProductId ?? null,
			quantity: item.qty,
			amount: getPrice(item.sku, 'backsplash', item.each),
			sku: item.sku,
			discount: 0,
			order,
			isFuelVariant: false
		});
		order++;
	});

	// Cast works around tsc resolving @rta/rta-blueprint-library through node_modules to the main clone's copy of the package in this worktree, not this branch's edited source
	itemData?.items.forEach((item: ItemizedLineItem & { isFuelVariant?: boolean }) => {
		// A hand-placed override shares a generic catalog sku, so without its typed-in label HubSpot stamps the placeholder product's name over the designer's (GitHub #513)
		const customName = item.isCustomOverride ? (item.description || item.label || '') : '';
		items.push({
			type: 'appliances',
			hsProductId: item.hsProductId ?? null,
			quantity: item.qty,
			amount: getPrice(item.sku, 'appliances', item.each),
			sku: item.sku,
			discount: 0,
			order,
			isFuelVariant: item.isFuelVariant ?? false,
			...(customName ? { name: customName } : {})
		});
		order++;
	});

	itemData?.accessories.forEach((item) => {
		items.push({
			type: 'accessories',
			hsProductId: item.hsProductId ?? null,
			quantity: item.qty,
			amount: getPrice(item.sku, 'accessories', item.each),
			sku: item.sku,
			discount: 0,
			order,
			isFuelVariant: false
		});
		order++;
	});

	promotionalData?.forEach((item) => {
		const price = item.hasDiscountRules ? item.totalAdjustment : (item.adjustmentType === 'FIXED' ? item.priceAdjustment : item.totalAdjustment);
		const roundedPrice = Math.round((price ?? 0) * 100) / 100;
		const itemType = item.isDiscount ? 'discount' : 'promotion';
		// Any promotional SKU whose description was edited away from its catalog default carries that text through as the HubSpot line item name — not just DISCOUNT (#352)
		const catalogEntry = PromotionalItemData.findBySku(item.sku);
		const catalogDescription = catalogEntry?.description ?? '';
		const isCustomDescription = Boolean(item.description) && item.description !== catalogDescription;
		// A sku the designer retyped in the estimate matches no catalog entry; send it so the quote shows what they typed rather than the product it is tied to by id (GitHub #513)
		const isCustomSku = !catalogEntry;
		items.push({
			type: itemType,
			hsProductId: item.hsProductId ?? null,
			quantity: 1,
			amount: roundedPrice < 0 ? 0 : roundedPrice,
			sku: item.sku,
			discount: roundedPrice < 0 ? Math.abs(roundedPrice) : 0,
			order,
			isFuelVariant: false,
			...(isCustomDescription ? { name: item.description } : {}),
			...(isCustomSku ? { customSku: item.sku } : {})
		});
		order++;
	});

	const customerCode = import.meta.env.VITE_APP_ENVIRONMENT === 'LIVE'
		? `${customer.firstName} ${customer.lastName}`
		: 'test test';

	const finalQuoteName = quoteName || customerCode;

	// Use the deal's address for tax calculation, fall back to customer if deal fields are empty
	const taxCustomer = dealAddress ? {
		...customer,
		address: dealAddress.address || customer.address,
		city: dealAddress.city || customer.city,
		state: dealAddress.state || customer.state,
		zipcode: dealAddress.zip || customer.zipcode
	} : customer;

	if (!taxCustomer.zipcode) {
		const message = 'Failed to calculate tax: no zipcode found on the deal. Please add a zipcode to the deal in HubSpot and try again.';
		if (setError) setError(message);
		return [null, message];
	}

	const [taxResponse, taxError] = await fetchTax({ kitchen: kitchenData, customer: taxCustomer, getToken });
	if (taxError || !taxResponse) return [null, taxError ?? 'Failed to calculate tax'];
	kitchenData.tax = taxResponse.totalTax;

	const quote = {
		customerCode,
		quoteName: finalQuoteName,
		dealId,
		link,
		taxBreakdown: taxResponse.taxBreakdown,
		items,
		discounts,
		fees,
		publish
	};

	try {
		const token = await getToken();
		const response = await axios.post(`${import.meta.env.VITE_API_URL}/create/quote`, quote, {
			headers: {
				Authorization: `Bearer ${token}`
			}
		});

		if (response.status === 200) {
			if (setQuoteId) setQuoteId(response.data);
			kitchenData.quoteId = response.data;
			return [response.data ?? null, null];
		}

		throw new Error('Error submitting quote');
	} catch (error) {
		if (setError) setError('Error submitting quote');
		return [null, 'Error submitting quote'];
	}

	return [null, null];
};
